/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale
} from "chart.js";
import { createEmptyBarChart } from "@/Pages/Dashboard/config";
import {
  StatusSummaryType,
  StatusWidgetDto
} from "@/Pages/Dashboard/shared/DeviceStatus/types";
import { Typography } from "@mui/material";

import { colors } from "@dotnet/telematics-design-system";

import { ResourceDownloadsStatus } from "../types";

Chart.register(BarController, BarElement, CategoryScale, LinearScale);

interface HorizontalBarChartProps {
  statusData: StatusWidgetDto | null;
  setHovered: Dispatch<SetStateAction<boolean>>;
  setHoveredElement: Dispatch<any>;
  setShowSingleStatusInfo: Dispatch<SetStateAction<StatusSummaryType | null>>;
}

const HorizontalBarChart = ({
  statusData,
  setHovered,
  setHoveredElement,
  setShowSingleStatusInfo
}: HorizontalBarChartProps) => {
  const { t } = useTranslation();

  const chartRef = useRef<any>(null);

  const [stats, setStats] = useState({
    onTime: 0,
    dueSoon: 0,
    overdue: 0,
    noDownloads: 0,
    total: 0
  });

  useEffect(() => {
    if (!statusData) return;

    const newStatusData = {
      onTime: 0,
      dueSoon: 0,
      overdue: 0,
      noDownloads: 0,
      total: statusData.totalCount
    };

    statusData.summary.forEach(item => {
      switch (item.name) {
        case ResourceDownloadsStatus.OnTime:
          newStatusData.onTime = item.count;
          break;
        case ResourceDownloadsStatus.DueSoon:
          newStatusData.dueSoon = item.count;
          break;
        case ResourceDownloadsStatus.Overdue:
          newStatusData.overdue = item.count;
          break;
        case ResourceDownloadsStatus.NoDownloads:
          newStatusData.noDownloads = item.count;
          break;
      }
    });

    setStats(newStatusData);
  }, [statusData]);

  const statusArray = [
    stats.onTime,
    stats.dueSoon,
    stats.overdue,
    stats.noDownloads
  ];

  const maxValue = Math.max(...statusArray.filter(v => v > 0), 0);
  const spacerValue = maxValue * 0.01; // tiny spacer

  const getBorderRadius = (index: number) =>
    statusArray[index] === 0
      ? 0
      : { topLeft: 4, bottomLeft: 4, topRight: 4, bottomRight: 4 };

  const baseDatasets = [
    {
      index: 0,
      label: ResourceDownloadsStatus.OnTime,
      value: stats.onTime,
      backgroundColor: colors.green700
    },
    {
      index: 2,
      label: ResourceDownloadsStatus.DueSoon,
      value: stats.dueSoon,
      backgroundColor: colors.orange400
    },
    {
      index: 4,
      label: ResourceDownloadsStatus.Overdue,
      value: stats.overdue,
      backgroundColor: colors.red700
    },
    {
      index: 6,
      label: ResourceDownloadsStatus.NoDownloads,
      value: stats.noDownloads,
      backgroundColor: colors.gray200,
      borderColor: colors.gray400,
      borderWidth: 1
    }
  ].filter(d => d.value > 0);

  const datasets: any[] = [];

  baseDatasets.forEach((item, idx) => {
    datasets.push({
      index: item.index,
      label: item.label,
      data: [item.value],
      backgroundColor: item.backgroundColor,
      hoverBackgroundColor: item.backgroundColor,
      barThickness: 16,
      borderSkipped: false,
      borderRadius: getBorderRadius(item.index),
      ...(item.borderColor && {
        borderColor: item.borderColor,
        borderWidth: item.borderWidth
      })
    });

    // spacer: either between bars or after single dataset
    if (idx < baseDatasets.length - 1 || baseDatasets.length === 1) {
      datasets.push({
        label: "",
        data: [spacerValue],
        backgroundColor: "transparent",
        barThickness: 0,
        isSpacer: true
      });
    }
  });

  const totalStack = statusArray.reduce((a, b) => a + b, 0);
  const numSpacers = baseDatasets.length > 1 ? baseDatasets.length - 1 : 1;
  const totalSpacers = spacerValue * numSpacers + 0.1;

  const data = {
    labels: ["Status"],
    datasets
  };

  const options: any = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        display: false,
        min: 0,
        max: totalStack + totalSpacers
      },
      y: { stacked: true, display: false }
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    onClick: (_: any, elements: any[]) => {
      if (!elements.length) return;

      const clicked = datasets[elements[0].datasetIndex];
      if (clicked.isSpacer) return;

      setShowSingleStatusInfo(
        statusData?.summary.find(i => i.name === clicked.label) ?? null
      );
    },
    onHover: (_: any, elements: any[], chart: any) => {
      const canvas = chart.canvas;
      if (!elements.length) {
        canvas.style.cursor = "default";
        setHovered(false);
        setHoveredElement(null);
        return;
      }

      const hovered = datasets[elements[0].datasetIndex];
      if (hovered.isSpacer) {
        canvas.style.cursor = "default";
        setHovered(false);
        setHoveredElement(null);
        return;
      }

      canvas.style.cursor = "pointer";
      setHovered(true);
      setHoveredElement(hovered.index);
    }
  };

  const emptyBarPlugin = useMemo(
    () => createEmptyBarChart(statusData?.totalCount),
    [statusData]
  );

  return (
    <div
      css={css({
        width: "100%",
        minHeight: 16,
        display: "flex",
        flexDirection: "column",
        gap: "4px"
      })}
      onMouseLeave={() => {
        setHovered(false);
        setHoveredElement(null);
      }}
    >
      <Typography variant="body3Medium" sx={{ color: colors.textSecondary }}>
        {t("Details##overview")}
      </Typography>
      <div
        css={css({
          width: "100%",
          height: "16px",
          alignItems: "center",
          padding: "4px 0"
        })}
      >
        <Bar
          id="horizontal-bar-chart"
          ref={chartRef}
          data={data}
          options={options}
          plugins={[emptyBarPlugin]}
        />
      </div>
    </div>
  );
};

export default HorizontalBarChart;
